import type { ID } from '@octopus-do/octopus-project-core'
import {
  InboxActions,
  type ProjectNotificationSettings,
  selectProjectSettings,
} from '@store/reducers/inbox'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// The select carries the override as a string; "default" is the null of the
// API, i.e. whatever the account says
const DEFAULT = 'default'

const toComments = (value: string): ProjectNotificationSettings['comments'] => {
  if (value === 'all' || value === 'mentions' || value === 'none') {
    return value
  }

  return null
}

const options = {
  comments: [
    { value: DEFAULT, label: 'Same as account' },
    { value: 'all', label: 'All' },
    { value: 'mentions', label: 'Mentions and replies' },
    { value: 'none', label: 'None' },
  ],
}

// The settings live in the inbox store, loaded by a saga when the modal
// opens. The switch shows what the server will do: the person's own choice
// for the project, or, until they made one, whether they took part in it
export const useProjectNotifications = (id: ID) => {
  const dispatch = useDispatch()
  const settings = useSelector(selectProjectSettings(id))

  useEffect(() => {
    dispatch(InboxActions.fetchProjectSettings({ projectId: id }))
  }, [id])

  const save = (next: ProjectNotificationSettings) =>
    dispatch(
      InboxActions.saveProjectSettings({ projectId: id, settings: next })
    )

  const comments = settings?.comments ?? null
  const digest = settings?.digest ?? null

  return {
    comments: comments ?? DEFAULT,
    digest: digest ?? settings?.digestDefault ?? false,
    options,
    onComments: (value: string) =>
      save({ comments: toComments(value), digest }),
    onDigest: (value: boolean) => save({ comments, digest: value }),
  }
}
